import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Bell, 
  Plus, 
  Eye, 
  Edit, 
  Trash2, 
  Hash, 
  Calendar, 
  MapPin,
  Users,
  AlertCircle,
  CheckCircle,
  Globe,
  FileText,
  Crown,
  Gavel,
  ArrowUp,
  ArrowDown,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import CreateNoticeModal from './CreateNoticeModal';
import ViewNoticeModal from './ViewNoticeModal';

const PublicNoticeBoard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('my-notices');
  const [myNotices, setMyNotices] = useState([]);
  const [publicNotices, setPublicNotices] = useState([]);
  const [noticeStats, setNoticeStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  
  // Scroll state management
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showScrollToolbar, setShowScrollToolbar] = useState(false);
  const [scrollContainerRef, setScrollContainerRef] = useState(null);

  // Fetch user's notices
  const fetchMyNotices = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notices/my-notices`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const notices = await response.json();
        setMyNotices(notices);
      }
    } catch (error) {
      console.error('Error fetching my notices:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch public notices
  const fetchPublicNotices = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notices/public?limit=20`);

      if (response.ok) {
        const notices = await response.json();
        setPublicNotices(notices);
      }
    } catch (error) {
      console.error('Error fetching public notices:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch notice statistics
  const fetchNoticeStats = async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('Fetching notice stats with token:', token ? token.substring(0, 20) + '...' : 'NO TOKEN');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notices/stats/my-stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Notice stats response status:', response.status);
      if (response.ok) {
        const stats = await response.json();
        console.log('Notice stats:', stats);
        setNoticeStats(stats);
      } else {
        console.error('Failed to fetch notice stats:', response.status);
      }
    } catch (error) {
      console.error('Error fetching notice stats:', error);
    }
  };

  useEffect(() => {
    console.log('PublicNoticeBoard: activeTab changed to:', activeTab);
    console.log('PublicNoticeBoard: user:', user);
    
    if (activeTab === 'my-notices') {
      if (user) {
        fetchMyNotices();
        fetchNoticeStats();
      } else {
        console.log('PublicNoticeBoard: User not loaded yet, waiting...');
      }
    } else if (activeTab === 'public-view') {
      // Navigate to the public notices page within the same app
      navigate('/public-notices');
    }
  }, [activeTab, user]);

  const handleNoticeCreated = () => {
    fetchMyNotices();
    fetchNoticeStats();
    setIsCreateModalOpen(false);
  };

  const handleViewNotice = (notice) => {
    setSelectedNotice(notice);
    setIsViewModalOpen(true);
  };

  const getPlanBadgeColor = (plan) => {
    switch (plan) {
      case 'free':
      case 'pro_se_starter':
        return 'bg-gray-100 text-gray-700';
      case 'justice_seeker':
        return 'bg-yellow-100 text-yellow-700';
      case 'sovereign_shield':
        return 'bg-red-100 text-red-700';
      case 'defender_of_the_people':
        return 'bg-purple-100 text-purple-700';
      default:
        return 'bg-blue-100 text-blue-700';
    }
  };

  const getNoticeTypeIcon = (type) => {
    switch (type) {
      case 'Affidavit of Status':
        return <FileText className="w-4 h-4" />;
      case 'Declaration of Private Trust':
        return <Crown className="w-4 h-4" />;
      case 'Cease & Desist Notice':
        return <AlertCircle className="w-4 h-4" />;
      case 'Administrative Default':
        return <Gavel className="w-4 h-4" />;
      default:
        return <Bell className="w-4 h-4" />;
    }
  };

  // Scroll handling functions
  const handleScroll = (e) => {
    const container = e.target;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    
    // Calculate scroll progress (0 to 100)
    const progress = scrollHeight > clientHeight ? (scrollTop / (scrollHeight - clientHeight)) * 100 : 0;
    setScrollProgress(progress);
    
    // Show toolbar when there's scrollable content
    setShowScrollToolbar(scrollHeight > clientHeight);
  };

  const scrollToTop = () => {
    if (scrollContainerRef) {
      scrollContainerRef.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const scrollToBottom = () => {
    if (scrollContainerRef) {
      scrollContainerRef.scrollTo({ top: scrollContainerRef.scrollHeight, behavior: 'smooth' });
    }
  };

  const scrollToPosition = (percentage) => {
    if (scrollContainerRef) {
      const maxScroll = scrollContainerRef.scrollHeight - scrollContainerRef.clientHeight;
      const targetScroll = (percentage / 100) * maxScroll;
      scrollContainerRef.scrollTo({ top: targetScroll, behavior: 'smooth' });
    }
  };

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Custom Scrollbar Styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
          transition: background 0.2s ease;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:active {
          background: #64748b;
        }
      `}</style>
      
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 flex items-center">
              <Globe className="w-7 h-7 text-blue-600 mr-3" />
              Public Notice Board
            </h1>
            <p className="text-gray-600 mt-1">"Put the World on Notice — Digitally, Lawfully, Sovereignly."</p>
          </div>
          
          {/* Statistics Card */}
          {noticeStats && (
            <div className="flex items-center space-x-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{noticeStats.total_notices}</div>
                <div className="text-xs text-gray-500">Total Notices</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600">{noticeStats.monthly_notices}</div>
                <div className="text-xs text-gray-500">This Month</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {noticeStats.plan_limit === -1 ? '∞' : noticeStats.remaining_posts}
                </div>
                <div className="text-xs text-gray-500">Remaining</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Subscription Status Banner */}
      {noticeStats && noticeStats.remaining_posts === 0 && noticeStats.plan_limit !== -1 && (
        <div className="mx-6 mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-yellow-800">Monthly Limit Reached</h3>
              <p className="text-sm text-yellow-700 mt-1">
                You've used all {noticeStats.plan_limit} notices for this month. 
                Upgrade your plan for more posting capacity.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex-1 flex flex-col min-h-0">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
          <TabsList className="grid w-full mx-6 mt-4 grid-cols-3 flex-shrink-0">
            <TabsTrigger value="my-notices" className="flex items-center space-x-2">
              <FileText className="w-4 h-4" />
              <span>My Notices</span>
            </TabsTrigger>
            <TabsTrigger value="public-view" className="flex items-center space-x-2">
              <Globe className="w-4 h-4" />
              <span>Public View</span>
            </TabsTrigger>
            <TabsTrigger value="create" className="flex items-center space-x-2">
              <Plus className="w-4 h-4" />
              <span>Create Notice</span>
            </TabsTrigger>
          </TabsList>

          <div 
            className="flex-1 overflow-y-auto px-6 pb-6 pt-4 min-h-0 custom-scrollbar" 
            ref={(ref) => setScrollContainerRef(ref)}
            onScroll={handleScroll}
          >
            {/* My Notices Tab */}
            <TabsContent value="my-notices" className="mt-0 space-y-4 h-full">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">My Published Notices</h2>
                <Button
                  onClick={() => setIsCreateModalOpen(true)}
                  disabled={noticeStats && noticeStats.remaining_posts === 0 && noticeStats.plan_limit !== -1}
                  className="flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Create New Notice</span>
                </Button>
              </div>

              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-500 mt-2">Loading notices...</p>
                </div>
              ) : myNotices.length === 0 ? (
                <div className="text-center py-12">
                  <Globe className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No notices yet</h3>
                  <p className="text-gray-500 mb-4">Create your first public notice to establish your lawful record</p>
                  <Button
                    onClick={() => setIsCreateModalOpen(true)}
                    disabled={noticeStats && noticeStats.remaining_posts === 0}
                  >
                    Create First Notice
                  </Button>
                </div>
              ) : (
                <div className="grid gap-4">
                  {myNotices.map((notice) => (
                    <Card key={notice.id} className="hover:shadow-md transition-shadow">
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center space-x-3">
                            {getNoticeTypeIcon(notice.notice_type)}
                            <div>
                              <CardTitle className="text-lg text-gray-900">{notice.title}</CardTitle>
                              <div className="flex items-center space-x-2 mt-1">
                                <Badge variant="outline" className="text-xs">
                                  {notice.notice_type}
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                  {notice.jurisdiction}
                                </Badge>
                                {notice.is_featured && (
                                  <Badge className="bg-yellow-100 text-yellow-700 text-xs">
                                    Featured
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewNotice(notice)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="flex items-center justify-between text-sm text-gray-500">
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-1">
                              <Calendar className="w-4 h-4" />
                              <span>{new Date(notice.created_at).toLocaleDateString()}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Eye className="w-4 h-4" />
                              <span>{notice.view_count} views</span>
                            </div>
                            {notice.hash_proof && (
                              <div className="flex items-center space-x-1">
                                <Hash className="w-4 h-4" />
                                <span className="font-mono text-xs">{notice.hash_proof.substring(0, 8)}...</span>
                              </div>
                            )}
                          </div>
                          <Badge 
                            className={`text-xs ${
                              notice.status === 'published' ? 'bg-green-100 text-green-700' : 
                              'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {notice.status}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Public View Tab */}
            <TabsContent value="public-view" className="mt-0 space-y-4 h-full">
              <div className="text-center py-12">
                <Globe className="w-16 h-16 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Navigating to Public Notices...</h3>
                <p className="text-gray-600 mb-4">
                  You will be taken to the public notices page where you can view all published notices from the community.
                </p>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              </div>
            </TabsContent>

            {/* Create Notice Tab */}
            <TabsContent value="create" className="mt-0 h-full">
              <div className="max-w-4xl mx-auto">
                <CreateNoticeModal
                  isOpen={true}
                  onClose={() => setActiveTab('my-notices')}
                  onNoticeCreated={handleNoticeCreated}
                  noticeStats={noticeStats}
                  embedded={true}
                />
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </div>

      {/* Custom Scroll Toolbar */}
      {showScrollToolbar && (
        <div className="fixed right-4 top-1/2 transform -translate-y-1/2 z-50 bg-white rounded-lg shadow-lg border border-gray-200 p-2 flex flex-col space-y-2">
          {/* Scroll to Top */}
          <Button
            variant="ghost"
            size="sm"
            onClick={scrollToTop}
            className="p-2 h-8 w-8 hover:bg-blue-50 hover:text-blue-600 transition-colors"
            title="Scroll to Top"
          >
            <ChevronUp className="w-4 h-4" />
          </Button>
          
          {/* Scroll Progress Indicator */}
          <div className="flex flex-col items-center py-2">
            <div className="w-1 h-20 bg-gray-200 rounded-full relative">
              <div 
                className="w-1 bg-blue-600 rounded-full transition-all duration-300 ease-out"
                style={{ height: `${scrollProgress}%` }}
              />
              <div 
                className="absolute w-3 h-3 bg-blue-600 rounded-full -left-1 transform -translate-y-1/2 shadow-sm transition-all duration-300 ease-out cursor-pointer hover:scale-110"
                style={{ top: `${scrollProgress}%` }}
                onMouseDown={(e) => {
                  const startY = e.clientY;
                  const startProgress = scrollProgress;
                  
                  const handleMouseMove = (e) => {
                    const deltaY = e.clientY - startY;
                    const progressChange = (deltaY / 80) * 100; // 80px is the height of the track
                    const newProgress = Math.max(0, Math.min(100, startProgress + progressChange));
                    scrollToPosition(newProgress);
                  };
                  
                  const handleMouseUp = () => {
                    document.removeEventListener('mousemove', handleMouseMove);
                    document.removeEventListener('mouseup', handleMouseUp);
                  };
                  
                  document.addEventListener('mousemove', handleMouseMove);
                  document.addEventListener('mouseup', handleMouseUp);
                }}
              />
            </div>
            <div className="text-xs text-gray-500 mt-1 font-mono">
              {Math.round(scrollProgress)}%
            </div>
          </div>
          
          {/* Scroll to Bottom */}
          <Button
            variant="ghost"
            size="sm"
            onClick={scrollToBottom}
            className="p-2 h-8 w-8 hover:bg-blue-50 hover:text-blue-600 transition-colors"
            title="Scroll to Bottom"
          >
            <ChevronDown className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Modals */}
      <CreateNoticeModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onNoticeCreated={handleNoticeCreated}
        noticeStats={noticeStats}
      />

      <ViewNoticeModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        notice={selectedNotice}
        isOwner={selectedNotice?.user_id === user?.id}
      />
    </div>
  );
};

export default PublicNoticeBoard;